import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Delete, Edit, VisibilityOutlined } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";
import { YuvagaazEmagazine } from "../../yuva_gaaz/yuvagaaz_data";

const EMPTY_FORM = {
  title: "",
  issue: "",
  description: "",
  releaseDate: "",
  coverImage: "",
};

function normalizeUrl(value: string) {
  const trimmedValue = value.trim();
  if (!trimmedValue) return "";
  if (trimmedValue.startsWith("http://") || trimmedValue.startsWith("https://") || trimmedValue.startsWith("/")) {
    return trimmedValue;
  }
  return `https://${trimmedValue}`;
}

export default function ManageEMagazinesTab() {
  const { showSnackbar } = useFeedback();
  const [items, setItems] = useState<YuvagaazEmagazine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<YuvagaazEmagazine | null>(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [selectedCoverImage, setSelectedCoverImage] = useState<File | null>(null);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);

  async function fetchItems() {
    try {
      setIsLoading(true);
      const snapshot = await getDocs(
        query(collection(firestore, "yuvagaaz_emagazines"), orderBy("releaseDate", "desc")),
      );
      setItems(snapshot.docs.map((itemDoc) => ({ docId: itemDoc.id, ...(itemDoc.data() as Omit<YuvagaazEmagazine, "docId">) })));
    } catch (error) {
      console.error("Failed to load e-magazines", error);
      showSnackbar("Failed to load e-magazines");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleEdit(item: YuvagaazEmagazine) {
    setEditingItem(item);
    setFormValues({
      title: item.title || "",
      issue: item.issue || "",
      description: item.description || "",
      releaseDate: item.releaseDate || "",
      coverImage: item.coverImage || "",
    });
    setSelectedCoverImage(null);
    setSelectedPdfFile(null);
  }

  async function handleUpdate() {
    if (!editingItem?.docId) return;
    try {
      let finalCoverImage = formValues.coverImage.trim();
      let finalPdfUrl = editingItem.pdfUrl;

      if (selectedCoverImage) {
        const coverRef = ref(storage, `yuvagaaz/emagazine-covers/${Date.now()}-${selectedCoverImage.name}`);
        await uploadBytes(coverRef, selectedCoverImage);
        finalCoverImage = await getDownloadURL(coverRef);
      }

      if (selectedPdfFile) {
        const pdfRef = ref(storage, `yuvagaaz/emagazines/${Date.now()}-${selectedPdfFile.name}`);
        await uploadBytes(pdfRef, selectedPdfFile);
        finalPdfUrl = await getDownloadURL(pdfRef);
      }

      await updateDoc(doc(firestore, "yuvagaaz_emagazines", editingItem.docId), {
        title: formValues.title.trim(),
        issue: formValues.issue.trim(),
        description: formValues.description.trim(),
        releaseDate: formValues.releaseDate.trim(),
        coverImage: normalizeUrl(finalCoverImage),
        pdfUrl: finalPdfUrl,
        updatedAt: new Date().toISOString(),
      });

      showSnackbar("E-magazine updated successfully");
      setEditingItem(null);
      setFormValues(EMPTY_FORM);
      setSelectedCoverImage(null);
      setSelectedPdfFile(null);
      fetchItems();
    } catch (error) {
      console.error("Failed to update e-magazine", error);
      showSnackbar("Failed to update e-magazine");
    }
  }

  async function handleDelete(item: YuvagaazEmagazine) {
    if (!item.docId) return;

    if (!window.confirm(`Delete "${item.title}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(firestore, "yuvagaaz_emagazines", item.docId));
      showSnackbar("E-magazine deleted successfully");
      fetchItems();
    } catch (error) {
      console.error("Failed to delete e-magazine", error);
      showSnackbar("Failed to delete e-magazine");
    }
  }

  if (isLoading) {
    return <Box py={6} textAlign="center"><Typography>Loading e-magazines...</Typography></Box>;
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        {items.map((item) => (
          <Grid item xs={12} sm={6} md={6} lg={4} key={item.docId || item.title}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <Box sx={{ width: "100%", height: 240, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#ffffff", borderBottom: "1px solid rgba(56, 136, 194, 0.12)", p: 2 }}>
                {item.coverImage ? (
                  <Box component="img" src={item.coverImage} alt={item.title} sx={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center center" }} />
                ) : (
                  <Typography variant="body2" color="text.secondary">No cover image</Typography>
                )}
              </Box>
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, flexGrow: 1 }}>
                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                {item.issue ? <Typography variant="body2" color="text.secondary">{item.issue}</Typography> : null}
                {item.releaseDate ? <Typography variant="body2" color="text.secondary">{new Date(item.releaseDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</Typography> : null}
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button variant="outlined" startIcon={<VisibilityOutlined />} onClick={() => window.open(item.pdfUrl, "_blank", "noopener,noreferrer")}>View</Button>
                  <Button variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(item)}>Edit</Button>
                  <Button color="error" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(item)}>Delete</Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(editingItem)} onClose={() => setEditingItem(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit E-Magazine</DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            <TextField fullWidth label="Title" value={formValues.title} onChange={(event) => setFormValues((prev) => ({ ...prev, title: event.target.value }))} />
            <TextField fullWidth label="Issue / Subtitle" value={formValues.issue} onChange={(event) => setFormValues((prev) => ({ ...prev, issue: event.target.value }))} />
            <TextField fullWidth multiline minRows={3} label="Description" value={formValues.description} onChange={(event) => setFormValues((prev) => ({ ...prev, description: event.target.value }))} />
            <TextField fullWidth label="Release Date" type="date" value={formValues.releaseDate} onChange={(event) => setFormValues((prev) => ({ ...prev, releaseDate: event.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField fullWidth label="Cover Image URL" value={formValues.coverImage} onChange={(event) => { setFormValues((prev) => ({ ...prev, coverImage: event.target.value })); if (event.target.value) setSelectedCoverImage(null); }} />
            <FormControl fullWidth>
              <FormLabel>Upload New Cover Image</FormLabel>
              <input type="file" accept="image/*" style={{ border: "1px solid rgb(181, 176, 176)", width: "96%", paddingLeft: 8, paddingRight: 8, paddingTop: 16, paddingBottom: 16, borderRadius: 5, marginTop: 8 }} onChange={(event) => { if (event.target.files?.[0]) { setSelectedCoverImage(event.target.files[0]); setFormValues((prev) => ({ ...prev, coverImage: "" })); } }} />
            </FormControl>
            <FormControl fullWidth>
              <FormLabel>Upload New PDF File</FormLabel>
              <input type="file" accept="application/pdf" style={{ border: "1px solid rgb(181, 176, 176)", width: "96%", paddingLeft: 8, paddingRight: 8, paddingTop: 16, paddingBottom: 16, borderRadius: 5, marginTop: 8 }} onChange={(event) => setSelectedPdfFile(event.target.files?.[0] || null)} />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingItem(null)} variant="outlined">Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
